import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, ThemeProvider, Toolbar, Typography, Button } from '@mui/material'
import { t } from 'i18next'
import { useSearchParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import EmailNotificationPreferences from 'src/components/Unsubscribe/EmailNotificationPreferences'
import { useDispatch, useSelector } from 'react-redux'
import {
  getPaymentRemindersSubscription,
  getPaymentUpdatesSubscription,
  setPaymentRemindersSubscription,
  setPaymentUpdatesSubscription,
} from 'src/store/unsubscribe/emailSubscriptionSlice'
import {
  UnsubscribeGroupEnum,
  UnsubscribeGroupsResponseViewModel,
} from '@rsmus/ecp-communicationservice'
import CustomErrorAlert from '../../components/forms/Alert/CustomErrorAlert'
import { Styles } from '../../types'
import { Heading } from '../../components/typography'
import NewRsmLogo from '../../components/media/NewRsmLogo'
import tokens from '../../styles/tokens.json'
import Footer from '../../components/layouts/Footer'
import { normalTheme } from '../../styles/materialTheme'
import { COMMUNICATIONSERVICE_BASE_URL } from '../../envVariables'

const styles: Styles = {
  toolbar: () => ({
    display: 'flex',
    height: '5.4375rem',
    alignContent: 'center',
    alignItems: 'center',
  }),
  toolbarRedesign: (theme) => ({
    display: 'flex',
    height: '5.4375rem',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    [theme.breakpoints.down('tablet')]: {
      justifyContent: 'flex-end',
    },
    [theme.breakpoints.down('desktop')]: {
      justifyContent: 'flex-end',
    },
  }),
  homeIcon: (theme) => ({
    position: 'absolute',
    left: 0,
    paddingBottom: '0.5rem',
    [theme.breakpoints.only('mobile')]: {
      paddingLeft: '0.5rem',
    },
    [theme.breakpoints.down('tablet')]: {
      '& svg': {
        width: '5rem',
      },
    },
    [theme.breakpoints.up('tablet')]: {
      top: '1.5rem',
    },
  }),
  toolbarContainer: (theme) => ({
    height: 'fit-content',
    backgroundColor: tokens.colors.black,
    backgroundImage: `url(${process.env.PUBLIC_URL}/myRSMHeaderBackground.svg)`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    [theme.breakpoints.only('tablet')]: {
      paddingLeft: '2rem',
      paddingRight: '2rem',
    },
    [theme.breakpoints.up('desktop')]: {
      paddingLeft: '6.5rem',
      paddingRight: '6.5rem',
    },
  }),
  unsubscribeContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
  },
  EmailPreferencesContainer: {
    backgroundColor: tokens.colors.white,
    paddingTop: '2.5rem',
    paddingBottom: '2.5rem',
    paddingLeft: '7.25rem',
    paddingRight: '7.25rem',
  },
  Heading: {
    fontSize: '3rem',
    lineHeight: '3.5rem',
    fontWeight: 300,
    fontFamily: 'Prelo-Book, sans-serif',
  },
  HeadingDescription: {
    fontFamily: 'Prelo-Book, sans-serif',
    fontSize: '1rem',
    lineHeight: '1.5rem',
    paddingTop: '1rem',
    paddingBottom: '2rem',
  },
  Email: {
    fontFamily: 'Prelo-Black, sans-serif',
    fontSize: '1rem',
    paddingBottom: '1.5rem',
    color: tokens.colors.rsmGray.copy,
  },
  mainContainer: (theme) => ({
    backgroundColor: theme.palette.rsmBackground.main,
    paddingBottom: '2.5rem',
    flex: 1,
  }),
}

const Unsubscribe = () => {
  const [searchParams] = useSearchParams()
  const emailId = searchParams.get('id') || ''
  const qs = searchParams.get('qs') || ''
  const navigate = useNavigate()

  const [reminders, setReminders] = useState(true)
  const [updates, setUpdates] = useState(true)
  const [error, setError] = useState<boolean>(false)
  const [success, setSuccess] = useState<boolean>(false)

  const dispatch = useDispatch()
  let abortController: AbortController | null = null
  const isPaymentReminderSuppressed = useSelector(
    getPaymentRemindersSubscription,
  )
  const isPaymentUpdateSuppressed = useSelector(getPaymentUpdatesSubscription)
  const fetchUnsubscribeGroups = useMemo(
    () =>
      async (
        callback: (
          results?: UnsubscribeGroupsResponseViewModel | undefined,
        ) => void,
      ) => {
        if (abortController) {
          abortController.abort('New request initiated')
        }
        abortController = new AbortController()
        const abortSignal = abortController.signal

        try {
          const response = await axios.get(
            `${COMMUNICATIONSERVICE_BASE_URL}/api/v1/Unsubscribe/GetUnsubscribeGroupsByEmail`,
            {
              params: { id: emailId, Qs: qs },
              signal: abortSignal,
            },
          )
          // Here, access nested data
          callback(response?.data?.data)
        } catch (err) {
          if (axios.isCancel(error)) {
            console.log('Request canceled:', error.message)
          } else {
            setError(true)
          }
          callback(undefined)
        }
      },
    [emailId, qs],
  )

  const emailUnsubscribe = async (
    request: { input: UnsubscribeGroupEnum },
    callback: (results?: boolean) => void,
  ) => {
    try {
      const res = await axios.post<boolean>(
        `${COMMUNICATIONSERVICE_BASE_URL}/api/v1/Unsubscribe/Unsubscribe`,
        {
          id: emailId,
          qs,
          unsubscribeGroups: [request.input],
        },
      )
      callback(res.data)
    } catch (err) {
      console.error('Unsubscribe failed:', err)
      callback(false)
    }
  }

  const emailSubscribe = async (
    request: { input: UnsubscribeGroupEnum },
    callback: (results?: boolean) => void,
  ) => {
    try {
      const res = await axios.post<boolean>(
        `${COMMUNICATIONSERVICE_BASE_URL}/api/v1/Unsubscribe/Subscribe`,
        {
          id: emailId,
          qs,
          unsubscribeGroups: [request.input],
        },
      )
      callback(res.data)
    } catch (err) {
      console.error('Subscribe failed:', err)
      callback(false)
    }
  }

  const onPaymentRemindersChange = useCallback(
    (value: boolean) => {
      if (!value) {
        dispatch(setPaymentRemindersSubscription(false))
      } else {
        dispatch(setPaymentRemindersSubscription(true))
      }
    },
    [dispatch],
  )

  const onPaymentUpdatesChange = useCallback(
    (value: boolean) => {
      if (!value) {
        dispatch(setPaymentUpdatesSubscription(false))
      } else {
        dispatch(setPaymentUpdatesSubscription(true))
      }
    },
    [dispatch],
  )

  useEffect(() => {
    if (success) {
      navigate('/unsubscribe-success', { replace: true })
    }
  }, [success, navigate])

  useEffect(() => {
    fetchUnsubscribeGroups(
      (results: UnsubscribeGroupsResponseViewModel | undefined) => {
        if (results && Array.isArray(results.suppressions)) {
          results.suppressions.forEach((suppression) => {
            if (suppression.id === UnsubscribeGroupEnum.PaymentReminders) {
              const subscribed = !suppression.suppressed
              dispatch(setPaymentRemindersSubscription(subscribed))
              setReminders(subscribed)
            } else if (suppression.id === UnsubscribeGroupEnum.PaymentUpdates) {
              const subscribed = !suppression.suppressed
              dispatch(setPaymentUpdatesSubscription(subscribed))
              setUpdates(subscribed)
            }
          })
        } else {
          setError(true)
        }
      },
    )
  }, [fetchUnsubscribeGroups, dispatch])

  const handleSubmit = async () => {
    const isPaymentReminderSuppressedChanged =
      isPaymentReminderSuppressed !== reminders

    const isPaymentUpdateSuppressedChanged =
      isPaymentUpdateSuppressed !== updates

    if (isPaymentReminderSuppressedChanged) {
      const request = { input: UnsubscribeGroupEnum.PaymentReminders }

      if (isPaymentReminderSuppressed) {
        await emailSubscribe(request, (results) => {
          if (results) {
            setSuccess(true)
          } else {
            setError(true)
          }
        })
      } else {
        await emailUnsubscribe(request, (results) => {
          if (results) {
            setSuccess(true)
          } else {
            setError(true)
          }
        })
      }
    }

    if (isPaymentUpdateSuppressedChanged) {
      const request = { input: UnsubscribeGroupEnum.PaymentUpdates }
      if (isPaymentUpdateSuppressed) {
        await emailSubscribe(request, (results) => {
          if (results) {
            setSuccess(true)
          } else {
            setError(true)
          }
        })
      } else {
        await emailUnsubscribe(request, (results) => {
          if (results) {
            setSuccess(true)
          } else {
            setError(true)
          }
        })
      }
    }
  }

  return (
    <ThemeProvider theme={normalTheme}>
      <Box sx={styles.unsubscribeContainer}>
        <Box sx={styles.mainContainer}>
          <Box sx={styles.toolbarContainer}>
            <Toolbar disableGutters sx={styles.toolbarRedesign}>
              <Box sx={styles.homeIcon}>
                <NewRsmLogo titleDescription="myRSM home" />
              </Box>
            </Toolbar>
          </Box>
          <Box sx={styles.EmailPreferencesContainer}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={styles.Heading}>
                <Heading variant="h1">{t('Unsubscribe.Heading')}</Heading>
              </Box>
              <Typography variant="body1" sx={styles.HeadingDescription}>
                {t('Unsubscribe.Description')}
              </Typography>
            </Box>

            <Box sx={styles.Email}>
              {t('Unsubscribe.Email')}
              <Box sx={{ paddingLeft: '0.5rem' }}>
                <Typography>{emailId}</Typography>
              </Box>
            </Box>

            <EmailNotificationPreferences
              onPaymentRemindersChange={onPaymentRemindersChange}
              onPaymentUpdatesChange={onPaymentUpdatesChange}
            />

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button variant="outlined" onClick={handleSubmit}>
                {t('Unsubscribe.UpdateSubscription')}
              </Button>
            </Box>
          </Box>
        </Box>
        {error && !success && (
          <CustomErrorAlert
            testId="Banner_Unsubscribe_Error"
            disableAutoHide
            open
            header="SupportError"
            message="Unsubscribe.ErrorMessage"
            close={() => setError(false)}
          />
        )}
        <Footer />
      </Box>
    </ThemeProvider>
  )
}

export default Unsubscribe
